import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Card,
  Grid,
  Chip,
  Skeleton,
  Alert,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useApiGet } from '../../../utils/httpClientTs';
import { getSPDPreworkList } from '../../../endpoints/annualReportsTs';
import {
  SPDPreworkListItem,
  SPDPreworkStatus,
} from '../../../types/annualReports';

const SPDPreworkList: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const { data, isLoading, error } = useApiGet(
    ['spd-prework-list'],
    () => getSPDPreworkList(),
    {
      enabled: true,
      retry: 1,
    }
  );

  const forms = data?.forms || [];

  // Filter forms based on search and status
  const filteredForms = forms.filter((form: SPDPreworkListItem) => {
    const matchesSearch = form.spd_chapter_name
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' || form.status_id === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (statusId: string) => {
    switch (statusId) {
      case SPDPreworkStatus.NOT_STARTED:
        return 'default';
      case SPDPreworkStatus.IN_PROGRESS:
        return 'warning';
      case SPDPreworkStatus.READY_FOR_REVIEW:
        return 'info';
      case SPDPreworkStatus.APPROVED:
        return 'success';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Not submitted';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Error loading SPD Prework forms. Please try again.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          SPD Prework
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage dues and assessments information for SPD chapters
        </Typography>
      </Box>

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            label="Search by Chapter"
            fullWidth
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value={SPDPreworkStatus.NOT_STARTED}>
                Not Started
              </MenuItem>
              <MenuItem value={SPDPreworkStatus.IN_PROGRESS}>
                In Progress
              </MenuItem>
              <MenuItem value={SPDPreworkStatus.READY_FOR_REVIEW}>
                Ready for Review
              </MenuItem>
              <MenuItem value={SPDPreworkStatus.APPROVED}>Approved</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Results */}
      {isLoading ? (
        <Grid container spacing={2}>
          {[...Array(6)].map((_, index) => (
            <Grid item xs={12} key={index}>
              <Card sx={{ p: 2 }}>
                <Skeleton variant="text" height={24} width="40%" />
                <Skeleton variant="text" height={20} width="60%" />
                <Skeleton variant="rectangular" height={32} width={100} />
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : filteredForms.length > 0 ? (
        <Grid container spacing={2}>
          {filteredForms.map((form: SPDPreworkListItem) => (
            <Grid item xs={12} key={form.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                  },
                }}
                onClick={() =>
                  navigate(`/annual-reports/prework/spd/${form.id}`)
                }
              >
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {form.spd_chapter_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Year: {form.year}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Chip
                        label={form.status_name}
                        color={getStatusColor(form.status_id) as any}
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        Submitted: {formatDate(form.submitted_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Typography variant="body2" color="text.secondary">
                        By: {form.submitted_by_name || 'Not yet submitted'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No forms found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria
          </Typography>
        </Card>
      )}
    </Box>
  );
};

export default SPDPreworkList;
